import { useEffect, useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';

import { USER_APPROVE_STATUS, USER_ROLE } from '@/config/constants';
import {
  useGetCurrentUserQuery,
  useUpdateUserLoanInfoMutation,
  useUpdateUserMutation,
} from '@/apis/extended/userApi';
import { usePurchasingPowerEstimation } from '@/hooks/usePurchasingPowerEstimation';
import { formatPrice } from '@/utils/helper';
import { CommonFormControl } from '../Common/CommonFormControl';
import { CommonNumberInput } from '../Common/CommonNumberInput';
import { UserApproveStatusBadge } from './components/UserApproveStatusBadge';

export interface ProfileAdvancedSettingsProps {
  /** `embedded` suppresses the outer page heading & extra spacing so the
   *  component renders cleanly inside another panel. Defaults to `page`. */
  variant?: 'page' | 'embedded';
}

export const ProfileAdvancedSettings = ({
  variant: _variant = 'page',
}: ProfileAdvancedSettingsProps = {}) => {
  const { data, isFetching } = useGetCurrentUserQuery();
  const [updateUser] = useUpdateUserMutation();
  const [updateUserLoanInfo] = useUpdateUserLoanInfoMutation();

  const [isUpdating, setIsUpdating] = useState(false);
  const [form, setForm] = useState({
    monthlyIncome: '',
    creditScore: '',
    downPayment: '',
    monthlyDebt: '',
    loanTerm: '',
  });

  const [lenderDefaultsForm, setLenderDefaultsForm] = useState({
    lenderName: '',
    lenderAddress: '',
    lenderNMLS: '',
    loanOfficerName: '',
    loanOfficerPhone: '',
    loanOfficerEmail: '',
    loanOfficerNMLS: '',
  });

  const purchasingPower = usePurchasingPowerEstimation({
    monthlyIncome: Number(form.monthlyIncome),
    // interestRate: Number(form.interestRate),
    downPayment: Number(form.downPayment),
    creditScore: Number(form.creditScore),
    monthlyDebt: Number(form.monthlyDebt),
    loanTerm: Number(form.loanTerm),
  });

  useEffect(() => {
    setForm({
      monthlyIncome: `${data?.loanInfo?.monthlyIncome || '0'}`,
      creditScore: `${data?.loanInfo?.creditScore || '0'}`,
      downPayment: `${data?.loanInfo?.downPayment || '0'}`,
      monthlyDebt: `${data?.loanInfo?.monthlyDebt || '0'}`,
      loanTerm: `${data?.loanInfo?.loanTerm || ''}`,
    });

    setLenderDefaultsForm({
      lenderName: data?.lenderDefaults?.lenderName || '',
      lenderAddress: data?.lenderDefaults?.lenderAddress || '',
      lenderNMLS: data?.lenderDefaults?.lenderNMLS || '',
      loanOfficerName: data?.lenderDefaults?.loanOfficerName || '',
      loanOfficerPhone: data?.lenderDefaults?.loanOfficerPhone || '',
      loanOfficerEmail: data?.lenderDefaults?.loanOfficerEmail || '',
      loanOfficerNMLS: data?.lenderDefaults?.loanOfficerNMLS || '',
    });
  }, [data]);

  const isUpdated = useMemo(() => {
    return (
      form.monthlyIncome !== data?.loanInfo?.monthlyIncome?.toString() ||
      form.creditScore !== data?.loanInfo?.creditScore?.toString() ||
      form.downPayment !== data?.loanInfo?.downPayment?.toString() ||
      form.monthlyDebt !== data?.loanInfo?.monthlyDebt?.toString() ||
      form.loanTerm !== data?.loanInfo?.loanTerm?.toString() ||
      lenderDefaultsForm.lenderName !==
        (data?.lenderDefaults?.lenderName || '') ||
      lenderDefaultsForm.lenderAddress !==
        (data?.lenderDefaults?.lenderAddress || '') ||
      lenderDefaultsForm.lenderNMLS !==
        (data?.lenderDefaults?.lenderNMLS || '') ||
      lenderDefaultsForm.loanOfficerName !==
        (data?.lenderDefaults?.loanOfficerName || '') ||
      lenderDefaultsForm.loanOfficerPhone !==
        (data?.lenderDefaults?.loanOfficerPhone || '') ||
      lenderDefaultsForm.loanOfficerEmail !==
        (data?.lenderDefaults?.loanOfficerEmail || '') ||
      lenderDefaultsForm.loanOfficerNMLS !==
        (data?.lenderDefaults?.loanOfficerNMLS || '')
    );
  }, [form, lenderDefaultsForm, data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    setIsUpdating(true);

    updateUser({
      occupancy: data?.occupancy || undefined,
      loanProgram: data?.loanProgram || undefined,
      loanEstimationType: data?.loanEstimationType || undefined,
      ...(data?.role === USER_ROLE.ADMIN && {
        lenderDefaults: {
          lenderName: lenderDefaultsForm.lenderName,
          lenderAddress: lenderDefaultsForm.lenderAddress,
          lenderNMLS: lenderDefaultsForm.lenderNMLS,
          loanOfficerName: lenderDefaultsForm.loanOfficerName,
          loanOfficerPhone: lenderDefaultsForm.loanOfficerPhone,
          loanOfficerEmail: lenderDefaultsForm.loanOfficerEmail,
          loanOfficerNMLS: lenderDefaultsForm.loanOfficerNMLS,
        },
      }),
    });

    await updateUserLoanInfo({
      monthlyIncome: form.monthlyIncome
        ? parseFloat(form.monthlyIncome)
        : undefined,
      creditScore: form.creditScore ? parseInt(form.creditScore) : undefined,
      downPayment: form.creditScore ? parseFloat(form.downPayment) : undefined,
      monthlyDebt: form.creditScore ? parseFloat(form.monthlyDebt) : undefined,
      loanTerm: parseInt(form.loanTerm),
    }).unwrap();

    setIsUpdating(false);
  };

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader
        title={
          <>
            Income and Loan Settings{' '}
            <UserApproveStatusBadge
              sx={{ ml: 2 }}
              status={data?.approveStatus}
              size="small"
            />
          </>
        }
        avatar={<i className="fa-light fa-screwdriver-wrench" />}
      />
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Set your income and loan details. Configure transaction mode, loan
          program, and occupancy on the <strong>Loan type</strong> tab.
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <CommonFormControl label="Monthly Income:" labelWidth={170}>
              <CommonNumberInput
                placeholder="Monthly Income"
                name="monthlyIncome"
                value={form.monthlyIncome}
                onChange={handleInputChange}
              />
            </CommonFormControl>
            <CommonFormControl
              label="Credit Score:"
              sx={{ mt: 1 }}
              labelWidth={170}
            >
              <CommonNumberInput
                placeholder="Credit Score"
                name="creditScore"
                value={form.creditScore}
                onChange={handleInputChange}
              />
            </CommonFormControl>
            <CommonFormControl
              label="Down Payment:"
              sx={{ mt: 1 }}
              labelWidth={170}
            >
              <CommonNumberInput
                placeholder="Down Payment"
                name="downPayment"
                value={form.downPayment}
                onChange={handleInputChange}
              />
            </CommonFormControl>
            <CommonFormControl
              label="Monthly Debt:"
              sx={{ mt: 1 }}
              labelWidth={170}
            >
              <CommonNumberInput
                placeholder="Monthly Debt"
                name="monthlyDebt"
                value={form.monthlyDebt}
                onChange={handleInputChange}
              />
            </CommonFormControl>
            <CommonFormControl
              label="Loan Term:"
              sx={{ mt: 1 }}
              labelWidth={170}
            >
              <Select
                placeholder="Loan Term"
                name="loanTerm"
                value={form.loanTerm}
                onChange={handleSelectChange}
              >
                <MenuItem value="15">15 Years</MenuItem>
                <MenuItem value="30">30 Years</MenuItem>
              </Select>
            </CommonFormControl>
            <CommonFormControl
              label="Purchasing Power:"
              labelWidth={170}
              secondLabel={purchasingPower ? formatPrice(purchasingPower) : '-'}
              sx={{ mt: 1 }}
            />
            {data?.approveStatus === USER_APPROVE_STATUS.APPROVED &&
              data?.approvalAmount && (
                <CommonFormControl
                  label="Approval Amount:"
                  labelWidth={170}
                  secondLabel={formatPrice(data.approvalAmount)}
                  sx={{ mt: 1 }}
                />
              )}
          </Grid>
        </Grid>

        {data?.role === USER_ROLE.ADMIN && (
          <Box sx={{ mt: 4 }}>
            <Card variant="outlined">
              <CardHeader
                title="Lender Defaults (Auto-fill Buyer Offers)"
                subheader="These values are used to prefill the buyer's Offer Specifics lender/loan officer section."
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Lender
                    </Typography>
                    <TextField
                      fullWidth
                      label="Lender Name"
                      value={lenderDefaultsForm.lenderName}
                      onChange={(e) =>
                        setLenderDefaultsForm((p) => ({
                          ...p,
                          lenderName: e.target.value,
                        }))
                      }
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Lender Address"
                      value={lenderDefaultsForm.lenderAddress}
                      onChange={(e) =>
                        setLenderDefaultsForm((p) => ({
                          ...p,
                          lenderAddress: e.target.value,
                        }))
                      }
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Lender NMLS ID"
                      value={lenderDefaultsForm.lenderNMLS}
                      onChange={(e) =>
                        setLenderDefaultsForm((p) => ({
                          ...p,
                          lenderNMLS: e.target.value,
                        }))
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Loan Officer
                    </Typography>
                    <TextField
                      fullWidth
                      label="Loan Officer Name"
                      value={lenderDefaultsForm.loanOfficerName}
                      onChange={(e) =>
                        setLenderDefaultsForm((p) => ({
                          ...p,
                          loanOfficerName: e.target.value,
                        }))
                      }
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Loan Officer Phone"
                      value={lenderDefaultsForm.loanOfficerPhone}
                      onChange={(e) =>
                        setLenderDefaultsForm((p) => ({
                          ...p,
                          loanOfficerPhone: e.target.value,
                        }))
                      }
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Loan Officer Email"
                      value={lenderDefaultsForm.loanOfficerEmail}
                      onChange={(e) =>
                        setLenderDefaultsForm((p) => ({
                          ...p,
                          loanOfficerEmail: e.target.value,
                        }))
                      }
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Loan Officer NMLS ID"
                      value={lenderDefaultsForm.loanOfficerNMLS}
                      onChange={(e) =>
                        setLenderDefaultsForm((p) => ({
                          ...p,
                          loanOfficerNMLS: e.target.value,
                        }))
                      }
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}

        <CommonFormControl sx={{ mt: 1 }} labelWidth={170}>
          <LoadingButton
            loading={isFetching || isUpdating}
            disabled={!isUpdated}
            variant="contained"
            startIcon={<i className="fa-light fa-pencil" />}
            onClick={handleSave}
          >
            Save
          </LoadingButton>
        </CommonFormControl>
      </CardContent>
    </Card>
  );
};
